import React from "react";
import InlineFieldTooltip from "./InlineFieldTooltip";
import { buildGeneralCostFormulaPreviewText } from "../utils/mobilizationSimulationUtils";

export const GENERAL_COST_CALC_TYPES = [
  { id: "qty_rate", label: "Quantity x Rate" },
  {
    id: "qty_rate_per_week_weeks",
    label: "Quantity x Rate per Week x # Weeks",
  },
  {
    id: "qty_rate_movement_frequency_days",
    label: "Quantity x Rate x Movements (by Frequency Days)",
  },
  {
    id: "qty_rate_hours_day_days",
    label: "Quantity x Rate x Hours/Day x # Days",
  },
];

const formatMoney = (value, decimals = 2) =>
  Number(value || 0).toFixed(decimals);

export const createEmptyGeneralCostForm = (year, currency = "CAD") => ({
  year,
  cost_category: "",
  description: "",
  calculation_type: "qty_rate",
  confined_space: false,
  quantity_scales_with_teams: false,
  currency,
  quantity: "1",
  rate: "0",
  rate_per_week: "0",
  hours_per_day: "0",
  day_count: "0",
  movement_frequency_days: "0",
  week_count: "0",
  exposure_class_ids: [],
  sort_order: "99",
});

function GeneralCostEditorForm({
  title,
  formData,
  setFormData,
  yearOptions,
  exposureClassOptions,
  exposureCodeById,
  defaultSystemCurrency,
  saving,
  isEditing,
  onClose,
  onSave,
  calculateGeneralCostTotal,
}) {
  const resolvedYearOptions = Array.from(
    new Set(
      [
        ...(Array.isArray(yearOptions) ? yearOptions : []),
        Number(formData?.year || new Date().getFullYear()),
      ].filter((year) => Number.isFinite(Number(year)) && Number(year) > 0),
    ),
  ).sort((left, right) => Number(right) - Number(left));

  return (
    <>
      <div className="flex items-center justify-between border-b px-6 py-4">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <span>{title}</span>
          <InlineFieldTooltip
            page="mobilization_costs"
            section="modal_titles"
            fieldKey="general_cost_form"
            fallbackText="Create or edit a general cost configuration row."
            title="General Cost Form"
          />
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700"
        >
          Close
        </button>
      </div>

      <div className="p-6 space-y-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm text-gray-700">Year</label>
            <select
              value={formData.year}
              onChange={(event) =>
                setFormData((previousValue) => ({
                  ...previousValue,
                  year: event.target.value,
                }))
              }
              className="w-full rounded border border-gray-300 px-3 py-2"
            >
              {resolvedYearOptions.map((year) => (
                <option key={`general-year-${year}`} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-700">
              Cost Name
            </label>
            <input
              type="text"
              value={formData.cost_category}
              onChange={(event) =>
                setFormData((previousValue) => ({
                  ...previousValue,
                  cost_category: event.target.value,
                }))
              }
              className="w-full rounded border border-gray-300 px-3 py-2"
              placeholder="e.g. Crew Accommodation"
            />
          </div>

          <div className="md:col-span-2">
            <label className="mb-1 block text-sm text-gray-700">
              Description
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(event) =>
                setFormData((previousValue) => ({
                  ...previousValue,
                  description: event.target.value,
                }))
              }
              className="w-full rounded border border-gray-300 px-3 py-2"
              placeholder="Optional"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-700">
              Calculation Type
            </label>
            <select
              value={formData.calculation_type}
              onChange={(event) =>
                setFormData((previousValue) => ({
                  ...previousValue,
                  calculation_type: event.target.value,
                }))
              }
              className="w-full rounded border border-gray-300 px-3 py-2"
            >
              {GENERAL_COST_CALC_TYPES.map((type) => (
                <option key={`general-calc-${type.id}`} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-700">
              Sort Order
            </label>
            <input
              type="number"
              step="1"
              value={formData.sort_order}
              onChange={(event) =>
                setFormData((previousValue) => ({
                  ...previousValue,
                  sort_order: event.target.value,
                }))
              }
              className="w-full rounded border border-gray-300 px-3 py-2"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-700">
              Confined Space
            </label>
            <select
              value={formData.confined_space ? "yes" : "no"}
              onChange={(event) =>
                setFormData((previousValue) => ({
                  ...previousValue,
                  confined_space: event.target.value === "yes",
                }))
              }
              className="w-full rounded border border-gray-300 px-3 py-2"
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-700">Currency</label>
            <select
              value={formData.currency}
              onChange={(event) =>
                setFormData((previousValue) => ({
                  ...previousValue,
                  currency: event.target.value,
                }))
              }
              className="w-full rounded border border-gray-300 px-3 py-2"
            >
              <option value="CAD">CAD</option>
              <option value="USD">USD</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-700">Quantity</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.quantity}
              onChange={(event) =>
                setFormData((previousValue) => ({
                  ...previousValue,
                  quantity: event.target.value,
                }))
              }
              className="w-full rounded border border-gray-300 px-3 py-2"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-700">
              Scale Quantity by Calculated Teams
            </label>
            <button
              type="button"
              onClick={() =>
                setFormData((previousValue) => ({
                  ...previousValue,
                  quantity_scales_with_teams:
                    !previousValue.quantity_scales_with_teams,
                }))
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.quantity_scales_with_teams
                  ? "bg-blue-600"
                  : "bg-gray-300"
              }`}
              title={
                formData.quantity_scales_with_teams ? "Enabled" : "Disabled"
              }
              aria-label="Toggle quantity scaling by calculated teams"
              role="switch"
              aria-checked={formData.quantity_scales_with_teams}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.quantity_scales_with_teams
                    ? "translate-x-6"
                    : "translate-x-1"
                }`}
              />
            </button>
            <span className="ml-3 align-middle text-sm text-gray-600">
              {formData.quantity_scales_with_teams ? "Enabled" : "Disabled"}
            </span>
          </div>

          {(formData.calculation_type === "qty_rate" ||
            formData.calculation_type === "qty_rate_movement_frequency_days" ||
            formData.calculation_type === "qty_rate_hours_day_days") && (
            <div>
              <label className="mb-1 block text-sm text-gray-700">Rate</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.rate}
                onChange={(event) =>
                  setFormData((previousValue) => ({
                    ...previousValue,
                    rate: event.target.value,
                  }))
                }
                className="w-full rounded border border-gray-300 px-3 py-2"
              />
            </div>
          )}

          {formData.calculation_type === "qty_rate_per_week_weeks" && (
            <>
              <div>
                <label className="mb-1 block text-sm text-gray-700">
                  Rate Per Week
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.rate_per_week}
                  onChange={(event) =>
                    setFormData((previousValue) => ({
                      ...previousValue,
                      rate_per_week: event.target.value,
                    }))
                  }
                  className="w-full rounded border border-gray-300 px-3 py-2"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700">
                  # Weeks
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.week_count}
                  onChange={(event) =>
                    setFormData((previousValue) => ({
                      ...previousValue,
                      week_count: event.target.value,
                    }))
                  }
                  className="w-full rounded border border-gray-300 px-3 py-2"
                />
              </div>
            </>
          )}

          {formData.calculation_type === "qty_rate_movement_frequency_days" && (
            <div>
              <label className="mb-1 block text-sm text-gray-700">
                Movement Frequency (days)
              </label>
              <input
                type="number"
                step="1"
                min="1"
                value={formData.movement_frequency_days}
                onChange={(event) =>
                  setFormData((previousValue) => ({
                    ...previousValue,
                    movement_frequency_days: event.target.value,
                  }))
                }
                className="w-full rounded border border-gray-300 px-3 py-2"
              />
            </div>
          )}

          {formData.calculation_type === "qty_rate_hours_day_days" && (
            <>
              <div>
                <label className="mb-1 block text-sm text-gray-700">
                  Hours/Day
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.hours_per_day}
                  onChange={(event) =>
                    setFormData((previousValue) => ({
                      ...previousValue,
                      hours_per_day: event.target.value,
                    }))
                  }
                  className="w-full rounded border border-gray-300 px-3 py-2"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm text-gray-700">
                  # Days
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.day_count}
                  onChange={(event) =>
                    setFormData((previousValue) => ({
                      ...previousValue,
                      day_count: event.target.value,
                    }))
                  }
                  className="w-full rounded border border-gray-300 px-3 py-2"
                />
              </div>
            </>
          )}

          <div className="md:col-span-2">
            <label className="mb-2 block text-sm text-gray-700">
              Exposure Classes
            </label>
            <div className="rounded border border-gray-300 bg-gray-50 p-3">
              <div className="flex flex-wrap gap-3">
                {(exposureClassOptions || []).map((option) => {
                  const optionId = Number(option.id);
                  const checked =
                    formData.exposure_class_ids.includes(optionId);
                  const code = option.categoryCode || option.category_code;

                  return (
                    <div
                      key={`exposure-opt-${option.id}`}
                      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 ${
                        checked
                          ? "border-blue-200 bg-blue-50"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      <span className="text-xs font-semibold text-gray-800">
                        {code}
                      </span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={checked}
                        aria-label={`Apply cost to ${code}`}
                        onClick={() => {
                          setFormData((previousValue) => {
                            const next = new Set(
                              previousValue.exposure_class_ids || [],
                            );
                            if (checked) next.delete(optionId);
                            else next.add(optionId);
                            return {
                              ...previousValue,
                              exposure_class_ids: Array.from(next),
                            };
                          });
                        }}
                        className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
                          checked ? "bg-blue-600" : "bg-gray-300"
                        }`}
                        title={`Toggle ${code}`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            checked ? "translate-x-5" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
              {(!exposureClassOptions || exposureClassOptions.length === 0) && (
                <div className="text-sm text-gray-500">
                  No exposure classes found.
                </div>
              )}
              <div className="mt-3 text-xs text-gray-600">
                {(formData.exposure_class_ids || []).length === 0
                  ? "This cost applies to all exposure classes when no toggle is enabled."
                  : `This cost applies only to: ${formData.exposure_class_ids
                      .map((id) => exposureCodeById.get(Number(id)) || `#${id}`)
                      .join(", ")}.`}
              </div>
            </div>
          </div>

          <div className="rounded border bg-gray-50 px-3 py-2 text-sm text-gray-700 md:col-span-2">
            <div>
              Formula Preview: {buildGeneralCostFormulaPreviewText(formData)}
            </div>
            <div className="mt-1">
              Estimated Total:{" "}
              {(() => {
                const converted = calculateGeneralCostTotal(formData, true);
                if (converted === null) {
                  return "Conversion rate unavailable";
                }
                return `$${formatMoney(converted)} ${defaultSystemCurrency}`;
              })()}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 border-t px-6 py-4">
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? "Saving..." : isEditing ? "Update Cost" : "Save Cost"}
        </button>
      </div>
    </>
  );
}

export default GeneralCostEditorForm;
